import React from 'react';
import {
  TableCell,
  TableRow,
  Box,
  TextField,
  Autocomplete,
} from '@mui/material';
import { getStoreIdSuggestions, getMaterialCodeSuggestions } from './PrepaidFormDetailState';
import FieldInfoTooltip from '../../Common/FieldInfoTooltip';
import {
  storeIdDescriptions,
  materialCodeDescriptions,
  quantityDescriptions
} from '../../Common/fieldDescriptions';
import { getQuantityMin, getQuantityMax, preventNonIntegerInput } from '../../../../utils/quantityValidation';

const PrepaidTestDataSection = ({
  section,
  items,
  formData,
  errors,
  isFieldDisabled,
  handleInputChange
}) => {
  return Object.entries(items).map(([item, fields]) => (
    <React.Fragment key={`${section}-${item}`}>
      <TableRow
        data-error={!!errors[section]?.[item]?.store_id || !!errors[section]?.[item]?.quantity || !!errors[section]?.[item]?.material_code}
        sx={{
          backgroundColor: (errors[section]?.[item]?.store_id || errors[section]?.[item]?.quantity || errors[section]?.[item]?.material_code) ? 'rgba(255, 0, 0, 0.05)' : 'inherit'
        }}
      >
        <TableCell>{item}</TableCell>
        {['store_id', 'material_code', 'quantity'].map(fieldName => {
          const fieldValue = fields[fieldName];
          return (
          <TableCell
            key={`${section}-${item}-${fieldName}`}
            sx={{
              ...(fieldName === 'store_id' && { width: '180px' })
            }}
          >
            {(item === 'MSISDN') && fieldName === 'material_code' ? (
              <Box>
                <Box sx={{ textAlign: 'center', fontWeight: 'bold', mb: 1 }}>
                  Material Code
                  <FieldInfoTooltip 
                    fieldName={materialCodeDescriptions.prepaid[item]?.title || "Material Code"}
                    description={materialCodeDescriptions.prepaid[item]?.description || "Material code information"}
                    examples={materialCodeDescriptions.prepaid[item]?.examples || []}
                    // variant="popper"
                  />
                </Box>
                <TextField
                  fullWidth
                  data-test={`${section}-${item}-${fieldName}`}
                  name={fieldName}
                  value={fieldValue}
                  disabled
                  InputProps={{
                    style: { backgroundColor: 'grey' }
                  }}
                />
              </Box>
            ) : (section === 'Reload' || section === 'Softpin') && fieldName === 'store_id' ? (
              <Box>
                <Box sx={{ textAlign: 'center', fontWeight: 'bold', mb: 1 }}>
                  Store ID
                  <FieldInfoTooltip 
                    fieldName={storeIdDescriptions.prepaid[`${section}_${item}`]?.title || storeIdDescriptions.prepaid[item]?.title || `Store ID for ${section}`}
                    description={storeIdDescriptions.prepaid[`${section}_${item}`]?.description || storeIdDescriptions.prepaid[item]?.description || "Store ID information"}
                    examples={storeIdDescriptions.prepaid[`${section}_${item}`]?.examples || storeIdDescriptions.prepaid[item]?.examples || []}
                    // variant="popper"
                  />
                </Box>
                <TextField
                  fullWidth
                  data-test={`${section}-${item}-${fieldName}`}
                  name={fieldName}
                  value={fieldValue}
                  disabled
                  InputProps={{
                    style: { backgroundColor: 'grey' }
                  }}
                />
              </Box>
            ) : fieldName === 'store_id' ? (
              <Box>
                <Box sx={{ textAlign: 'center', fontWeight: 'bold', mb: 1 }}>
                  Store ID
                  <FieldInfoTooltip 
                    fieldName={storeIdDescriptions.prepaid[item]?.title || `Store ID for ${item}`}
                    description={storeIdDescriptions.prepaid[item]?.description || "Store ID information"}
                    examples={storeIdDescriptions.prepaid[item]?.examples || []}
                    variant="popper"
                  />
                </Box>
                {fieldValue === 'Others' ? (
                  <TextField data-test={`${section}-${item}-${fieldName}`} />
                ) : (
                  <Autocomplete
                    freeSolo
                    disabled={isFieldDisabled(section, item)}
                    options={!isFieldDisabled(section, item) ? getStoreIdSuggestions(formData.general.environment) : []}
                    value={fieldValue || ''}
                    onChange={(_, newValue) => {
                      if (!isFieldDisabled(section, item)) {
                        const e = { target: { name: fieldName, value: newValue || '' } };
                        handleInputChange(e, section, item);
                        
                        // Check if this is a custom entry (not in predefined store IDs)
                        const isPredefined = getStoreIdSuggestions(formData.general.environment).includes(newValue);
                        
                        // Show warning only for custom entries (but still allow them)
                        if (newValue && !isPredefined) {
                          const warningEvent = { target: { name: 'store_id_warning', value: 'Warning: The store ID entered may be incorrect' } };
                          handleInputChange(warningEvent, section, item);
                        } else {
                          // Clear warning for predefined entries
                          const clearWarningEvent = { target: { name: 'store_id_warning', value: '' } };
                          handleInputChange(clearWarningEvent, section, item);
                        }
                      } else {
                        // Clear field value when it's disabled
                        const e = { target: { name: fieldName, value: '' } };
                        handleInputChange(e, section, item);
                      }
                    }}
                    onInputChange={(_, newValue) => {
                      if (!isFieldDisabled(section, item)) {
                        const e = { target: { name: fieldName, value: newValue || '' } };
                        handleInputChange(e, section, item);
                        
                        // Check if this is a custom entry while typing
                        const isPredefined = getStoreIdSuggestions(formData.general.environment).includes(newValue);
                        
                        // Show warning only for custom entries while typing
                        if (newValue && !isPredefined) {
                          const warningEvent = { target: { name: 'store_id_warning', value: 'Warning: The store ID entered may be incorrect' } };
                          handleInputChange(warningEvent, section, item);
                        } else {
                          // Clear warning for predefined entries
                          const clearWarningEvent = { target: { name: 'store_id_warning', value: '' } };
                          handleInputChange(clearWarningEvent, section, item);
                        }
                      }
                    }}
                    ListboxProps={{
                      style: { maxHeight: '150px' }
                    }}
                    PopperProps={{
                      style: { width: 'fit-content', minWidth: '230px' }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        data-test={`${section}-${item}-${fieldName}`}
                        fullWidth
                        error={!!errors[section]?.[item]?.store_id}
                        helperText={errors[section]?.[item]?.store_id || formData?.[section]?.[item]?.store_id_warning}
                        InputProps={{
                          ...params.InputProps,
                          style: isFieldDisabled(section, item) ? { backgroundColor: 'grey' } : {}
                        }}
                        disabled={isFieldDisabled(section, item)}
                      />
                    )}
                  />
                )}
              </Box>
            ) : fieldName === 'material_code' ? (
              <Box>
                <Box sx={{ textAlign: 'center', fontWeight: 'bold', mb: 1 }}>
                  Material Code
                  <FieldInfoTooltip 
                    fieldName={materialCodeDescriptions.prepaid[`${section}_${item}`]?.title || materialCodeDescriptions.prepaid[item]?.title || `Material Code for ${item}`}
                    description={materialCodeDescriptions.prepaid[`${section}_${item}`]?.description || materialCodeDescriptions.prepaid[item]?.description || "Material code information"}
                    examples={materialCodeDescriptions.prepaid[`${section}_${item}`]?.examples || materialCodeDescriptions.prepaid[item]?.examples || []}
                    variant="popper"
                  />
                </Box>
                {fieldValue === 'Others' ? (
                  <TextField
                    data-test={`${section}-${item}-${fieldName}`}
                    fullWidth
                    placeholder="Material Code"
                    value={fieldValue || ''}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      const event = { target: { name: fieldName, value: newValue } };
                      handleInputChange(event, section, item);
                    }}
                    error={!!errors[section]?.[item]?.material_code}
                    helperText={errors[section]?.[item]?.material_code || formData?.[section]?.[item]?.material_code_warning}
                    disabled={isFieldDisabled(section, item)}
                  />
                ) : (
                  <Autocomplete
                    freeSolo
                    disabled={isFieldDisabled(section, item)}
                    options={!isFieldDisabled(section, item) ? getMaterialCodeSuggestions(section, item) : []}
                    value={fieldValue || ''}
                    onChange={(_, newValue) => {
                      if (!isFieldDisabled(section, item)) {
                        const e = { target: { name: fieldName, value: newValue || '' } };
                        handleInputChange(e, section, item);
                        
                        // Check if this is a custom entry (not in predefined material codes)
                        const isPredefined = getMaterialCodeSuggestions(section, item).includes(newValue);
                        
                        // Show warning only for custom entries (but still allow them)
                        if (newValue && !isPredefined) {
                          const warningEvent = { target: { name: 'material_code_warning', value: 'Warning: The material code entered may be incorrect' } };
                          handleInputChange(warningEvent, section, item);
                        } else {
                          // Clear warning for predefined entries
                          const clearWarningEvent = { target: { name: 'material_code_warning', value: '' } };
                          handleInputChange(clearWarningEvent, section, item);
                        }
                      } else {
                        // Clear field value when it's disabled
                        const e = { target: { name: fieldName, value: '' } };
                        handleInputChange(e, section, item);
                      }
                    }}
                    onInputChange={(_, newValue) => {
                      if (!isFieldDisabled(section, item)) {
                        const e = { target: { name: fieldName, value: newValue || '' } };
                        handleInputChange(e, section, item);
                        
                        // Check if this is a custom entry while typing
                        const isPredefined = getMaterialCodeSuggestions(section, item).includes(newValue);
                        
                        // Show warning only for custom entries while typing
                        if (newValue && !isPredefined) {
                          const warningEvent = { target: { name: 'material_code_warning', value: 'Warning: The material code entered may be incorrect' } };
                          handleInputChange(warningEvent, section, item);
                        } else {
                          // Clear warning for predefined entries
                          const clearWarningEvent = { target: { name: 'material_code_warning', value: '' } };
                          handleInputChange(clearWarningEvent, section, item);
                        }
                      }
                    }}
                    ListboxProps={{
                      style: { maxHeight: '150px' }
                    }}
                    PopperProps={{
                      style: { width: 'fit-content', minWidth: '230px' }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        data-test={`${section}-${item}-${fieldName}`}
                        fullWidth
                        error={!!errors[section]?.[item]?.material_code}
                        helperText={errors[section]?.[item]?.material_code || formData?.[section]?.[item]?.material_code_warning}
                        InputProps={{
                          ...params.InputProps,
                          style: isFieldDisabled(section, item) ? { backgroundColor: 'grey' } : {}
                        }}
                        disabled={isFieldDisabled(section, item)}
                      />
                    )}
                  />
                )}
              </Box>
            ) : (
              <Box>
                <Box sx={{ textAlign: 'center', fontWeight: 'bold', mb: 1 }}>
                  Quantity
                  <FieldInfoTooltip
                    fieldName={quantityDescriptions.prepaid[item]?.title || quantityDescriptions.general.title}
                    description={quantityDescriptions.prepaid[item]?.description || quantityDescriptions.general.description}
                    examples={quantityDescriptions.prepaid[item]?.examples || quantityDescriptions.general.examples}
                    // variant="popper"
                  />
                </Box>
                <TextField
                  fullWidth
                  data-test={`${section}-${item}-${fieldName}`}
                  name={fieldName}
                  value={fieldValue}
                  type="number"
                  inputProps={{
                    min: getQuantityMin('prepaid', item),
                    max: getQuantityMax('prepaid', item),
                    step: 1
                  }}
                  onChange={(e) => {
                    if (!isFieldDisabled(section, item)) {
                      handleInputChange(e, section, item);
                    } else {
                      // Clear field value when it's disabled
                      const event = { target: { name: fieldName, value: '' } };
                      handleInputChange(event, section, item);
                    }
                  }}
                  disabled={isFieldDisabled(section, item)}
                  error={fieldName === 'quantity' && !!errors[section]?.[item]?.quantity}
                  helperText={fieldName === 'quantity' ? errors[section]?.[item]?.quantity : ''}
                  InputProps={{
                    style: isFieldDisabled(section, item) ? { backgroundColor: 'grey' } : {}
                  }}
                  onKeyDown={(e) => {
                    if (isFieldDisabled(section, item)) {
                      e.preventDefault();
                      return false;
                    }
                    // Prevent non-integer input (decimal points, etc.)
                    preventNonIntegerInput(e);
                  }}
                />
              </Box>
            )}
          </TableCell>
        );
        })}
      </TableRow>
    </React.Fragment>
  ));
};

export default PrepaidTestDataSection;